import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const filter = createFilterOptions({
	trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact = null }) => {
	const history = useHistory();

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [subject, setSubject] = useState("");
	const [queueOptions, setQueueOptions] = useState([]);
	const [whatsappOptions, setWhatsappOptions] = useState([]);
	const [selectedQueueId, setSelectedQueueId] = useState("");
	const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
	const [loadingOptions, setLoadingOptions] = useState(false);
	const { user } = useContext(AuthContext);

	const filteredWhatsappOptions = selectedQueueId
		? whatsappOptions.filter(option => {
				const queueIds = Array.isArray(option.queueIds) ? option.queueIds : [];
				return queueIds.length === 0 || queueIds.includes(Number(selectedQueueId));
		  })
		: whatsappOptions;

	useEffect(() => {
		if (!modalOpen) return;

		if (initialContact?.id) {
			setSelectedContact(initialContact);
			setOptions([initialContact]);
			setSearchParam(initialContact.name || initialContact.number || "");
		}
	}, [modalOpen, initialContact]);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	useEffect(() => {
		let isMounted = true;

		const loadNewTicketOptions = async () => {
			if (!modalOpen) return;

			setLoadingOptions(true);
			try {
				const { data } = await api.get("/tickets/new-options");
				if (!isMounted) return;

				const queues = Array.isArray(data?.queues) ? data.queues : [];
				const whatsapps = Array.isArray(data?.whatsapps) ? data.whatsapps : [];
				const defaults = data?.defaults || {};

				setQueueOptions(queues);
				setWhatsappOptions(whatsapps);
				setSelectedQueueId(
					defaults.queueId ? String(defaults.queueId) : queues.length === 1 ? String(queues[0].id) : ""
				);
				setSelectedWhatsappId(
					defaults.whatsappId
						? String(defaults.whatsappId)
						: whatsapps.length === 1
						? String(whatsapps[0].id)
						: ""
				);
			} catch (err) {
				if (isMounted) {
					setQueueOptions([]);
					setWhatsappOptions([]);
					setSelectedQueueId("");
					setSelectedWhatsappId("");
					toastError(err);
				}
			} finally {
				if (isMounted) {
					setLoadingOptions(false);
				}
			}
		};

		loadNewTicketOptions();

		return () => {
			isMounted = false;
		};
	}, [modalOpen]);

	useEffect(() => {
		if (!selectedWhatsappId) return;

		const isStillValid = filteredWhatsappOptions.some(
			option => String(option.id) === String(selectedWhatsappId)
		);

		if (!isStillValid) {
			setSelectedWhatsappId("");
		}
	}, [filteredWhatsappOptions, selectedWhatsappId]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setSubject("");
		setSelectedQueueId("");
		setSelectedWhatsappId("");
		setQueueOptions([]);
		setWhatsappOptions([]);
	};

	const handleSaveTicket = async contactId => {
		if (!contactId) return;
		setLoading(true);
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				userId: user.id,
				status: "open",
				subject: subject.trim(),
				queueId: selectedQueueId ? Number(selectedQueueId) : undefined,
				whatsappId: selectedWhatsappId ? Number(selectedWhatsappId) : undefined,
			});
			history.push(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
		handleClose();
	};

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number) {
			setSelectedContact(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = contact => {
		handleSaveTicket(contact.id);
	};

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

	const renderOption = option => {
		if (!option) return "";
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${i18n.t("newTicketModal.add")} ${option.name}`;
		}
	};

	const renderOptionLabel = option => {
		if (!option) return "";
		if (typeof option === "string") return option;
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${option.name}`;
		}
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			></ContactModal>
			<Dialog open={modalOpen} onClose={handleClose}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("newTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Autocomplete
						options={options}
						value={selectedContact}
						loading={loading}
						style={{ width: 300 }}
						disabled={Boolean(initialContact?.id)}
						clearOnBlur
						autoHighlight
						freeSolo
						clearOnEscape
						getOptionLabel={renderOptionLabel}
						renderOption={renderOption}
						filterOptions={createAddContactOption}
						onChange={(e, newValue) => handleSelectOption(e, newValue)}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("newTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus
								onChange={e => setSearchParam(e.target.value)}
								onKeyPress={e => {
									if (loading || !selectedContact) return;
									else if (e.key === "Enter") {
										handleSaveTicket(selectedContact.id);
									}
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
					<TextField
						margin="dense"
						label="Assunto do chamado"
						variant="outlined"
						fullWidth
						value={subject}
						onChange={e => setSubject(e.target.value)}
						style={{ marginTop: 16 }}
					/>
					<TextField
						margin="dense"
						label="Fila"
						variant="outlined"
						select
						fullWidth
						value={selectedQueueId}
						onChange={e => setSelectedQueueId(e.target.value)}
						style={{ marginTop: 16 }}
						disabled={loadingOptions}
						helperText={
							loadingOptions
								? "Carregando filas permitidas..."
								: queueOptions.length === 0
								? "Nenhuma fila disponível para seu perfil."
								: ""
						}
					>
						<MenuItem value="">
							Selecione a fila
						</MenuItem>
						{queueOptions.map(queue => (
							<MenuItem key={queue.id} value={String(queue.id)}>
								{queue.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						margin="dense"
						label="Conexão"
						variant="outlined"
						select
						fullWidth
						value={selectedWhatsappId}
						onChange={e => setSelectedWhatsappId(e.target.value)}
						style={{ marginTop: 16 }}
						disabled={loadingOptions}
						helperText={
							loadingOptions
								? "Carregando conexões permitidas..."
								: filteredWhatsappOptions.length === 0
								? "Nenhuma conexão disponível para seu perfil."
								: ""
						}
					>
						<MenuItem value="">
							Selecione a conexão
						</MenuItem>
						{filteredWhatsappOptions.map(whatsapp => (
							<MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
								{whatsapp.name}
							</MenuItem>
						))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{i18n.t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={
							!selectedContact ||
							!selectedQueueId ||
							!selectedWhatsappId ||
							loadingOptions
						}
						onClick={() => handleSaveTicket(selectedContact.id)}
						color="primary"
						loading={loading}
					>
						{i18n.t("newTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
